import { ArrowForwardIos, DateRange, FileDownload, Person, Phone, Phone as PhoneIcon } from "@mui/icons-material";
import { BookingStatus, BookingType, HubModel, OrderMode, OrderModel, RideStatus, SubscriptionInvoiceStatus } from '../utils/Models';
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { DateTime } from 'luxon';
import { Colors } from '../../config/Constants';
import { Box } from "@mui/system";
import { BookingStateFilters, SearchFilters } from "./BookingsDashboard";
import { Fragment, useState } from "react";
import { GobikesApi } from '../../config/Apis';
import { getAuthCookie } from '../auth/AuthUtil';
import { billingCycleDisplayLabel } from '../utils/CommonUtils';
import { RecordCashPayment } from './RecordCashPayment';

export const bookingStatusToColor = {
  [BookingStatus.PENDING]: Colors.toastWarningText,
  [BookingStatus.FAILED]: Colors.toastFailureText,
  [BookingStatus.CONFIRMED]: Colors.toastSuccessText,
  [BookingStatus.EXTENDED]: Colors.toastSuccessText,
  [BookingStatus.CANCELLED]: Colors.toastFailureText
}
export const bookingStatusToBgColor = {
  [BookingStatus.PENDING]: Colors.toastWarningBg,
  [BookingStatus.FAILED]: Colors.toastFailureBg,
  [BookingStatus.CONFIRMED]: Colors.toastSuccessBg,
  [BookingStatus.EXTENDED]: Colors.toastSuccessBg,
  [BookingStatus.CANCELLED]: Colors.toastFailureBg
}
const orderModeToColor = {
  [OrderMode.USER]: Colors.toastSuccessText,
  [OrderMode.ADMIN]: Colors.toastSuccessText,
  [OrderMode.VENDOR]: Colors.toastWarningText
}
const orderModeToBgColor = {
  [OrderMode.USER]: Colors.toastSuccessBg,
  [OrderMode.ADMIN]: Colors.toastSuccessBg,
  [OrderMode.VENDOR]: Colors.toastWarningBg
}
const rideStatusToText = {
  [RideStatus.NOT_STARTED]: 'NOT STARTED',
  [RideStatus.STARTED]: 'STARTED',
  [RideStatus.ENDED]: 'ENDED'
}
const getBookingSourceText = (row: OrderModel) => row.bookingChannel === 'OTHER'
  ? (row.bookingChannelOther || 'Other')
  : (row.bookingChannel === 'GOWHEELO' ? 'GoWheelo' : (row.bookingChannel || ''));

// Subscription health - derived from the effective due date (oldest outstanding invoice, else nextBillingDate), never stored
type SubscriptionHealth = 'HEALTHY' | 'DUE_TOMORROW' | 'PENDING' | 'OVERDUE';
const subscriptionHealthToColor: { [k in SubscriptionHealth]: string } = {
  HEALTHY: Colors.toastSuccessText,
  DUE_TOMORROW: Colors.toastWarningText,
  PENDING: '#e65100',
  OVERDUE: Colors.toastFailureText
};
const subscriptionHealthToBgColor: { [k in SubscriptionHealth]: string } = {
  HEALTHY: Colors.toastSuccessBg,
  DUE_TOMORROW: Colors.toastWarningBg,
  PENDING: '#fff3e0',
  OVERDUE: Colors.toastFailureBg
};
const subscriptionHealthToLabel: { [k in SubscriptionHealth]: string } = {
  HEALTHY: 'HEALTHY',
  DUE_TOMORROW: 'RENEWAL DUE TOMORROW',
  PENDING: 'PAYMENT PENDING',
  OVERDUE: 'OVERDUE'
};
const subscriptionHealthToAction: { [k in SubscriptionHealth]?: string } = {
  DUE_TOMORROW: 'Send Reminder',
  PENDING: 'Send Payment Link',
  OVERDUE: 'Send Payment Link'
};

// Get the outstanding (unpaid) amounts for an order - both cron/manual per-cycle invoices and
// unpaid bulk extension payments (e.g. a payment link sent via Extend Booking that hasn't been paid yet).
// Excludes unpaid rows whose period ends at/before nextBillingDate - those are abandoned/superseded
// attempts for a period that's already been paid for via a different extension/invoice.
function getOutstandingInvoices(row: OrderModel): { dueDate: number, amount: number }[] {
  const paidThrough = Number(row.nextBillingDate) || 0;
  return [
    ...(row.subscription_invoices || [])
      .filter(i => i.status !== SubscriptionInvoiceStatus.PAID && Number(i.cycleEnd) > paidThrough)
      .map(i => ({ dueDate: Number(i.dueDate), amount: i.amount })),
    ...(row.order_extensions || [])
      .filter(e => !e.paymentDone && !e.voided && Number(e.extEnd) > paidThrough)
      .map(e => ({ dueDate: Number(e.extStart), amount: e.extraAmount }))
  ];
}

// The date subscription health/sorting is computed against - the oldest outstanding invoice's due date
// if one exists, else the order's own nextBillingDate (so a badge can go yellow before an invoice is generated).
function getSubscriptionEffectiveDate(row: OrderModel): number {
  const outstanding = getOutstandingInvoices(row);
  return outstanding.length ? Math.min(...outstanding.map(i => Number(i.dueDate))) : Number(row.nextBillingDate);
}

// Derive subscription health from the effective due date.
// > 1 day away = Healthy, due tomorrow = Due Tomorrow, overdue <3 days = Pending, overdue >3 days = Overdue.
function getSubscriptionHealth(row: OrderModel): SubscriptionHealth {
  const effectiveDate = getSubscriptionEffectiveDate(row);
  if (!effectiveDate) return 'HEALTHY';
  const dayMs = 24 * 60 * 60 * 1000;
  const daysUntil = (effectiveDate - Date.now()) / dayMs;
  if (daysUntil > 1) return 'HEALTHY';
  if (daysUntil > 0) return 'DUE_TOMORROW';
  const daysOverdue = -daysUntil;
  if (daysOverdue > 3) return 'OVERDUE';
  return 'PENDING';
}

// Billing pill color - driven purely by whether there's an outstanding balance, not due-date urgency
const outstandingToColor = (amount: number) => amount > Number.EPSILON ? Colors.toastFailureText : Colors.toastSuccessText;
const outstandingToBgColor = (amount: number) => amount > Number.EPSILON ? Colors.toastFailureBg : Colors.toastSuccessBg;

function pendingSubscriptionOwesMoney(row: OrderModel): boolean {
  return row.bookingType === BookingType.SUBSCRIPTION
    && row.bookingStatus === BookingStatus.PENDING
    && Number(row.outstandingAmount || 0) > 0;
}

// Booking grid component
export function BookingsDataGrid(
  props: {
    ordersList: OrderModel[],
    setParentState: any,
    containerClass: any,
    searchFilters: { field: SearchFilters, keyword: string },
    stateFilters: { [k in BookingStateFilters]?: boolean },
    hubId?: number,
    hubsById?: { [hubId: number]: HubModel },
    showHubColumn?: boolean,
    refreshOrders?: () => void,
    setToastState?: (s: { open: boolean, message?: string }) => void
  }
) {
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<number | null>(null);
  const [subscriptionActionLoadingId, setSubscriptionActionLoadingId] = useState<number | null>(null);

  const handleSubscriptionAction = async (e: React.MouseEvent, orderId: number, action: 'send-payment-link') => {
    e.stopPropagation();
    setSubscriptionActionLoadingId(orderId);
    try {
      const authCookie = getAuthCookie();
      const { data } = await GobikesApi.post(
        `/vendors/hubs/${props.hubId}/orders/${orderId}/subscription/${action}`,
        {},
        { headers: { 'vendor-token': authCookie } }
      );
      if (data?.data?.paymentLink) {
        navigator.clipboard.writeText(data.data.paymentLink);
        props.setToastState?.({ open: true, message: 'Payment link copied! Share it with the customer.' });
      }
      props.refreshOrders?.();
    } catch (e) {
      props.setToastState?.({ open: true, message: 'Failed to complete action, please retry' });
    }
    setSubscriptionActionLoadingId(null);
  };

  const handleDownloadInvoice = async (orderId: number) => {
    setInvoiceLoadingId(orderId);
    try {
      const authCookie = getAuthCookie();
      const response = await GobikesApi.get(
        `/vendors/hubs/${props.hubId}/orders/${orderId}/invoice`,
        { headers: { 'vendor-token': authCookie }, responseType: 'blob' }
      );
      if (response?.data) {
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${orderId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      // silent fail
    }
    setInvoiceLoadingId(null);
  };

  // Search filters
  let searchFilterOperator = 'contains';
  let searchFilterColumn = 'id';
  if (props.searchFilters.field === SearchFilters.PHONE_NUMBER) {
    searchFilterColumn = 'userContact';
  } else if (props.searchFilters.field === SearchFilters.RC_NUMBER) {
    searchFilterColumn = 'rcNumbers';
  } else if (props.searchFilters.field === SearchFilters.CUSTOMER_NAME) {
    searchFilterColumn = 'userName';
  }

  // State filters
  let filteredList = (props.ordersList || [])
  const filterRequired = Boolean(Object.values(props.stateFilters).find(f => f)) && filteredList.length;
  if (filterRequired) {
    // Utility vars
    const dayStart = DateTime.fromMillis(new Date().getTime()).startOf('day').toMillis();
    const dayEnd = DateTime.fromMillis(new Date().getTime()).endOf('day').toMillis();
    const tomorrowStart = DateTime.fromMillis(new Date().getTime()).plus({ days: 1 }).startOf('day').toMillis();
    const tomorrowEnd = DateTime.fromMillis(new Date().getTime()).plus({ days: 1 }).endOf('day').toMillis();
    const currentTime = new Date().getTime();
    // Filter data
    filteredList = (props.ordersList || []).filter(o => {
      const orderConfirmed = isOrderConfirmed(o);
      if (props.stateFilters[BookingStateFilters.ALL]) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.ONGOING_TIME] && orderConfirmed && o.rideStartScheduled < currentTime && o.rideEndScheduled > currentTime) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.ONGOING_STATUS] && orderConfirmed && o.rideStatus === RideStatus.STARTED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.UPCOMING_TIME] && orderConfirmed && o.rideStartScheduled > currentTime) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.UPCOMING_STATUS] && orderConfirmed && o.rideStatus === RideStatus.NOT_STARTED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.CANCELLED] && o.bookingStatus === BookingStatus.CANCELLED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.EXTENDED] && o.bookingStatus === BookingStatus.EXTENDED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.COMPLETED_TIME] && orderConfirmed && o.rideEndScheduled < currentTime) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.COMPLETED_STATUS] && orderConfirmed && o.rideStatus === RideStatus.ENDED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TODAY_PICKUPS] && orderConfirmed && o.rideStartScheduled > dayStart && o.rideStartScheduled < dayEnd) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TODAY_PENDING_PICKUPS] && orderConfirmed && o.rideStartScheduled > dayStart && o.rideStartScheduled < dayEnd && o.rideStatus === RideStatus.NOT_STARTED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TODAY_DROPS] && orderConfirmed && o.rideEndScheduled > dayStart && o.rideEndScheduled < dayEnd) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TODAY_PENDING_DROPS] && orderConfirmed && o.rideEndScheduled > dayStart && o.rideEndScheduled < dayEnd && o.rideStatus !== RideStatus.ENDED) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TOMORROW_PICKUPS] && orderConfirmed && o.rideStartScheduled > tomorrowStart && o.rideStartScheduled < tomorrowEnd) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.TOMORROW_DROPS] && orderConfirmed && o.rideEndScheduled > tomorrowStart && o.rideEndScheduled < tomorrowEnd) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.SUBSCRIPTION_ORDERS] && o.bookingType === BookingType.SUBSCRIPTION) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.SUBSCRIPTION_HEALTHY] && o.bookingType === BookingType.SUBSCRIPTION && getSubscriptionHealth(o) === 'HEALTHY') {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE] && o.bookingType === BookingType.SUBSCRIPTION && ['DUE_TOMORROW', 'PENDING'].includes(getSubscriptionHealth(o))) {
        return true;
      }
      if (props.stateFilters[BookingStateFilters.SUBSCRIPTION_OVERDUE] && o.bookingType === BookingType.SUBSCRIPTION && getSubscriptionHealth(o) === 'OVERDUE') {
        return true;
      }
      return false;
    });

    // Sort data
    if (props.stateFilters[BookingStateFilters.TODAY_PICKUPS] || props.stateFilters[BookingStateFilters.TODAY_PENDING_PICKUPS] || props.stateFilters[BookingStateFilters.TOMORROW_PICKUPS] || props.stateFilters[BookingStateFilters.UPCOMING_TIME] || props.stateFilters[BookingStateFilters.UPCOMING_STATUS]) {
      filteredList.sort((a, b) => a.rideStartScheduled - b.rideStartScheduled);
    }
    else if (props.stateFilters[BookingStateFilters.TODAY_DROPS] || props.stateFilters[BookingStateFilters.TODAY_PENDING_DROPS] || props.stateFilters[BookingStateFilters.TOMORROW_DROPS] || props.stateFilters[BookingStateFilters.ONGOING_TIME] || props.stateFilters[BookingStateFilters.ONGOING_STATUS]) {
      filteredList.sort((a, b) => a.rideEndScheduled - b.rideEndScheduled);
    } else if (props.stateFilters[BookingStateFilters.EXTENDED]) {
      filteredList.sort((a, b) => Math.max(...[Number(b.paymentTime), ...(b.order_extensions ? b.order_extensions.map(e => e.paymentTime || 0) : [])]) - Math.max(...[Number(a.paymentTime), ...(a.order_extensions ? a.order_extensions.map(e => e.paymentTime || 0) : [])]));
    } else if (props.stateFilters[BookingStateFilters.SUBSCRIPTION_ORDERS] || props.stateFilters[BookingStateFilters.SUBSCRIPTION_HEALTHY] || props.stateFilters[BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE] || props.stateFilters[BookingStateFilters.SUBSCRIPTION_OVERDUE]) {
      // Soonest due date (end date) first
      filteredList.sort((a, b) => getSubscriptionEffectiveDate(a) - getSubscriptionEffectiveDate(b));
    }

  }

  // Whether the "Subscription" toggle (any subscription-related filter) is active
  const isSubscriptionView = Boolean(
    props.stateFilters[BookingStateFilters.SUBSCRIPTION_ORDERS]
    || props.stateFilters[BookingStateFilters.SUBSCRIPTION_HEALTHY]
    || props.stateFilters[BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE]
    || props.stateFilters[BookingStateFilters.SUBSCRIPTION_OVERDUE]
  );

  // GataGrid props
  const dgProps = {
    disableColumnMenu: true,
    disableColumnFilter: true,
    className: 'gbk-dg',
    rowHeight: 50,
    pageSize: 10,
    filterModel: {
      items: [{
        columnField: searchFilterColumn, value: props.searchFilters.keyword, operatorValue: searchFilterOperator
      }],
    },
    columnVisibilityModel: {
      rcNumbers: false,
      rideStartScheduled: false,
      rideEndScheduled: false,
      source: false,
      userContact: false,
      hubName: Boolean(props.showHubColumn),
      subscriptionBilling: isSubscriptionView
    },
    columns: [{
      field: 'id',
      headerName: 'Booking ID',
      width: 115,
      renderCell: (o: { row: OrderModel, value: any }) => (
        <span>
          {o.row.bookingType === BookingType.SUBSCRIPTION && (
            <span title="Subscription" style={{ marginRight: '4px' }}>🔄</span>
          )}
          <b>{o.value}</b>
        </span>
      ),
      sortable: false
    }, {
      field: 'hubName',
      headerName: 'Hub',
      width: 140,
      align: 'center',
      valueGetter: (o: { row: OrderModel }) => props.hubsById?.[o.row.hubId]?.landmark || '',
      sortable: false
    }, {
      field: 'userName',
      headerName: 'Customer Name',
      width: 150,
      align: 'center',
      valueGetter: (o: { row: OrderModel }) => `${o.row?.user?.firstName || ''} ${o.row?.user?.lastName || ''}`,
      sortable: false
    }, {
      field: 'bikeName',
      headerName: 'Vehicle Name',
      width: 220,
      renderCell: o => {
        const bike = o.row.bikes[0];
        const startingMethod = bike.startingMethod === 'AUTO' ? 'Automatic' : bike.startingMethod;
        const engineType = bike.engineType?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
        return (
          <div style={{ lineHeight: 1.3, textAlign: 'center' }}>
            <b>{`${bike.brand} ${bike.name}`}</b>
            {bike.vehicleType === 'CAR' && (
              <div style={{ fontSize: '0.75em', color: '#888' }}>{`(${startingMethod}, ${engineType})`}</div>
            )}
          </div>
        );
      },
      sortable: false,
      align: 'center'
    }, {
      field: 'rcNumbers',
      headerName: 'RC Numbers',
      width: 100,
      valueGetter: (o: { row: OrderModel }) => `${o.row.bikeDetails && o.row.bikeDetails.length ? o.row.bikeDetails.map(b => b.rcNumber).join(',') : ''}`,
      sortable: false
    }, {
      field: 'rideStartScheduled',
      headerName: 'Pickup Date',
      width: 180,
      align: 'center',
      valueFormatter: o => DateTime.fromMillis(Number(o.value)).toLocaleString(DateTime.DATETIME_MED),
      sortable: false
    }, {
      field: 'rideEndScheduled',
      headerName: 'Drop Date',
      width: 180,
      align: 'center',
      valueFormatter: o => DateTime.fromMillis(Number(o.value)).toLocaleString(DateTime.DATETIME_MED),
      sortable: false
    }, {
      field: 'pickupAndDrop',
      headerName: 'Pickup Date - Drop Date',
      width: 320,
      align: 'center',
      valueGetter: (o: { row: OrderModel }) => `${DateTime.fromMillis(Number(o.row.rideStartScheduled)).toLocaleString(DateTime.DATETIME_MED)} - ${DateTime.fromMillis(Number(o.row.rideEndScheduled)).toLocaleString(DateTime.DATETIME_MED)}`,
      sortable: false
    }, {
      field: 'totalRentalAmount',
      headerName: 'Total Amount',
      width: 135,
      align: 'center',
      valueFormatter: o => `₹ ${Number(o.value).toFixed(2)}`,
      renderCell: (data: { row: OrderModel }) => <b>₹ {Number(data.row.currentTotalPayableAmount).toFixed(2)}</b>,
      sortable: false
    }, {
      field: 'userContact',
      headerName: 'Contact',
      width: 150,
      align: 'center',
      renderCell: o => <span ><a href={`tel:${o.row?.user?.phone}`} style={{ color: '#212121', textDecoration: 'none' }}><PhoneIcon />&nbsp;{o.row?.user?.phone}</a></span>,
      valueGetter: o => o.row?.user?.phone,
      sortable: false
    }, {
      field: 'bookingStatus',
      headerName: 'Status',
      headerClassName: 'pad-right-60',
      width: 220,
      renderCell: (o: { row: OrderModel }) => (
        <div style={{
          textAlign: 'left', width: '100%'
        }}>
          {o.row.bookingType === BookingType.SUBSCRIPTION && o.row.bookingStatus !== BookingStatus.PENDING ? (
            <div
              style={{
                border: `1px solid ${subscriptionHealthToBgColor[getSubscriptionHealth(o.row)]}`,
                color: subscriptionHealthToColor[getSubscriptionHealth(o.row)],
                background: subscriptionHealthToBgColor[getSubscriptionHealth(o.row)],
                borderRadius: '20px',
                padding: '3px 6px',
                fontSize: '10px',
                fontWeight: 700,
                width: '110px',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                display: 'inline-block'
              }}
            >
              {subscriptionHealthToLabel[getSubscriptionHealth(o.row)]}
            </div>
          ) : (
            <div
              style={{
                border: `1px solid ${pendingSubscriptionOwesMoney(o.row) ? subscriptionHealthToBgColor.OVERDUE : bookingStatusToBgColor[o.row.bookingStatus]}`,
                color: pendingSubscriptionOwesMoney(o.row) ? subscriptionHealthToColor.OVERDUE : bookingStatusToColor[o.row.bookingStatus],
                background: pendingSubscriptionOwesMoney(o.row) ? subscriptionHealthToBgColor.OVERDUE : bookingStatusToBgColor[o.row.bookingStatus],
                borderRadius: '20px',
                padding: '3px 6px',
                fontSize: '10px',
                fontWeight: 700,
                width: '110px',
                textAlign: 'center',
                display: 'inline-block'
              }
              }
            >
              {getOrderStatusText(o.row.bookingStatus, o.row.rideStartScheduled, o.row.rideEndScheduled)}
            </div >
          )}
          {
            o.row.orderMode === OrderMode.VENDOR && getBookingSourceText(o.row) ? (
              <div style={{
                textAlign: 'center',
                borderRadius: '20px',
                padding: '3px 10px',
                fontSize: '11px',
                fontWeight: 700,
                border: '1px solid #444',
                backgroundColor: '#444',
                color: '#fff',
                display: 'inline-block',
                whiteSpace: 'nowrap',
                marginLeft: '10px'
              }}>
                {getBookingSourceText(o.row)}
              </div>
            ) : ''
          }

        </div >
      ),
      // <span style={{ color: bookingStatusToColor[o.row.bookingStatus], fontWeight: 700 }}>{`${o.row.bookingStatus}`}</span>,
      sortable: false,
      align: 'center'
    }, {
      field: 'subscriptionBilling',
      headerName: 'Billing',
      width: 190,
      align: 'center',
      headerAlign: 'center',
      renderCell: (o: { row: OrderModel }) => {
        if (o.row.bookingType !== BookingType.SUBSCRIPTION) {
          return <div style={{ display: 'flex', justifyContent: 'center', width: '100%', color: '#ccc' }}>-</div>;
        }
        const outstandingAmount = Number(o.row.outstandingAmount || 0);
        const outstanding = getOutstandingInvoices(o.row);
        const dueDate = outstanding.length ? Math.min(...outstanding.map(i => i.dueDate)) : o.row.nextBillingDate;
        return (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <div
              style={{
                textAlign: 'center',
                border: `1px solid ${outstandingToBgColor(outstandingAmount)}`,
                background: outstandingToBgColor(outstandingAmount),
                borderRadius: '10px',
                padding: '4px 10px',
                lineHeight: 1.5
              }}
            >
              <div style={{ fontSize: '10px', color: '#666' }}>
                {dueDate ? `Next Billing: ${DateTime.fromMillis(Number(dueDate)).toFormat('MMM d')}` : 'No cycle due'}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: outstandingToColor(outstandingAmount), whiteSpace: 'nowrap' }}>
                Outstanding: ₹{outstandingAmount.toFixed(2)}
              </div>
            </div>
          </div>
        );
      },
      sortable: false
    }, {
      field: 'rideStatus',
      headerName: 'Ride',
      headerClassName: 'pad-right-60',
      width: 150,
      renderCell: (o: { row: OrderModel }) => (
        <div style={{
          textAlign: 'left', width: '100%'
        }}>

          {o.row.bookingStatus !== BookingStatus.CANCELLED ? (
            <div
              style={{
                border: `1px solid #444`,
                color: '#444',
                backgroundColor: '#fff',
                borderRadius: '20px',
                padding: '3px 10px',
                fontSize: '11px',
                fontWeight: 700,
                width: '100px',
                textAlign: 'center',
                display: 'inline-block'
              }}
            >
              {o.row.bookingStatus === BookingStatus.PENDING && o.row.bookingType !== BookingType.SUBSCRIPTION
                ? 'NOT STARTED'
                : rideStatusToText[o.row.rideStatus]}
            </div>
          ) : ''}
        </div >
      ),
      sortable: false,
      align: 'center'
    }, {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: (o: { row: OrderModel }) => <div
        style={{ float: 'right', border: `1px solid ${orderModeToBgColor[o.row.orderMode]}`, color: orderModeToColor[o.row.orderMode], background: orderModeToBgColor[o.row.orderMode], borderRadius: '4px', padding: '2px 6px', marginTop: '-2px' }}
      >
        {o.row.orderMode === OrderMode.VENDOR ? 'SELF' : 'GoBikes'}
      </div>,
      sortable: false,
      align: 'center'
    },
      // {
      //   field: 'actions',
      //   headerName: ' ',
      //   width: 10,
      //   renderCell: o => (
      //     <div
      //       style={{ color: Colors.primary, cursor: 'pointer', textAlign: 'center', margin: 'auto' }}
      //       onClick={() => props.setParentState({ error: '', loading: false, view: 'individual', orderData: o.row })}>
      //       <span style={{ display: 'inline-block', borderRadius: '30px', border: `1px solid ${Colors.primary}`, height: '16px', width: '18px', padding: '4px', paddingTop: '6px' }} className="gbk-icon-span fs-14 hoverable hoverable-primary"><ArrowForwardIosOutlined /></span>
      //     </div>
      //   ),
      //   sortable: false
      // }
    {
      field: 'invoice',
      headerName: 'Invoice',
      width: 120,
      sortable: false,
      align: 'center',
      renderCell: (o: { row: OrderModel }) => {
        const isConfirmed = o.row.bookingStatus === BookingStatus.CONFIRMED || o.row.bookingStatus === BookingStatus.EXTENDED;
        if (!isConfirmed) return null;
        const loading = invoiceLoadingId === o.row.id;
        return (
          <div
            onClick={(e) => { e.stopPropagation(); if (!loading) handleDownloadInvoice(o.row.id); }}
            style={{ cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.4 : 1 }}
          >
            <FileDownload style={{ color: '#444', fontSize: '22px' }} />
          </div>
        );
      }
    }] as GridColumns
  }

  // Standard rental phone card (existing layout, unchanged)
  function renderStandardRow(row: OrderModel) {
    return (
      <Box className='gbk-dg-row-phone'>
        {/* Header — subtle status-tinted gradient */}
        <Box
          className='gbk-dg-row-phone-header'
          style={{ background: `linear-gradient(90deg, ${bookingStatusToBgColor[row.bookingStatus]}70 0%, #fafafa 55%)` }}
        >
          <span style={{ flex: 1, fontSize: '12px', color: '#555' }}>Order <b style={{ color: '#111', fontSize: '13px' }}>#{row.id}</b></span>
          {row.orderMode === OrderMode.VENDOR && getBookingSourceText(row) && (
            <div style={{ background: '#f0f0f0', color: '#555', borderRadius: '6px', padding: '2px 7px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{getBookingSourceText(row)}</div>
          )}
          {row.bookingStatus !== BookingStatus.CANCELLED && (
            <div style={{ background: '#3a3a3a', color: '#fff', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {row.bookingStatus === BookingStatus.PENDING && row.bookingType !== BookingType.SUBSCRIPTION
                ? 'NOT STARTED'
                : rideStatusToText[row.rideStatus]}
            </div>
          )}
          <div style={{ background: bookingStatusToBgColor[row.bookingStatus], color: bookingStatusToColor[row.bookingStatus], borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
            {getOrderStatusText(row.bookingStatus, row.rideStartScheduled, row.rideEndScheduled)}
          </div>
        </Box>

        {/* Body */}
        <Box
          className='gbk-dg-row-phone-body'
          onClick={() => props.setParentState({ error: '', loading: false, view: 'individual', orderData: row })}
        >
          {/* Bike row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={row?.bikes?.[0]?.image} style={{ height: '40px', width: 'auto', flexShrink: 0, borderRadius: '4px' }} alt="bike" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1a1a1a' }}>
                {row?.bikes?.[0]?.name}
              </div>
              <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>Qty: {row?.bikes?.[0]?.order_bikes?.quantity}</span>
                {row.bikeDetails?.length ? (
                  <span style={{ background: '#4a4a4a', color: '#fff', borderRadius: '4px', padding: '0 5px', fontSize: '10px' }}>
                    {row.bikeDetails.slice(0, 1).map(b => b.rcNumber).join(', ')}{row.bikeDetails.length > 1 ? ` +${row.bikeDetails.length - 1}` : ''}
                  </span>
                ) : ''}
              </div>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
              <b style={{ fontSize: '15px', color: Colors.primary }}>₹{Number(row.currentTotalPayableAmount).toFixed(2)}</b>
              <ArrowForwardIos style={{ fontSize: '11px', color: Colors.primary }} />
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={{ borderTop: '1px solid #f2f2f2', my: '9px' }} />

          {/* Customer info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '5px' }}>
            <Person style={{ fontSize: '14px', color: Colors.primary }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: Colors.primaryDark }}>
              {row.user?.firstName} {row.user?.lastName}
            </span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '9px' }}>
            <Phone style={{ fontSize: '14px', color: Colors.primary }} />
            <a href={`tel:${row.user?.phone}`} style={{ fontSize: '13px', color: Colors.primaryDark, fontWeight: 600, textDecoration: 'none' }}>
              {row.user?.phone}
            </a>
            {row.emPhone && (
              <a href={`tel:${row.emPhone}`} style={{ fontSize: '12px', color: '#999' }}>, {row.emPhone}</a>
            )}
          </Box>

          {/* Dates pill — subtle brand gradient */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #eef8d8 0%, #f7f7f7 100%)', border: '1px solid #ddeebb', borderRadius: '8px', padding: '6px 10px', fontSize: '11px', color: '#556' }}>
            <DateRange style={{ fontSize: '13px', color: Colors.primary, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{DateTime.fromMillis(Number(row.rideStartScheduled)).toFormat('MMM d, h:mm a')}</span>
            <ArrowForwardIos style={{ fontSize: '9px', color: Colors.primary, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{DateTime.fromMillis(Number(row.rideEndScheduled)).toFormat('MMM d, h:mm a')}</span>
          </Box>
        </Box>
      </Box>
    );
  }

  // Subscription rental phone card - health badge in place of status chip, cycle info instead of raw dates
  function renderSubscriptionRow(row: OrderModel) {
    const isPendingConfirmation = row.bookingStatus === BookingStatus.PENDING;
    const health = getSubscriptionHealth(row);
    const outstanding = getOutstandingInvoices(row);
    const outstandingAmount = Number(row.outstandingAmount || 0);
    const pendingOwesMoney = pendingSubscriptionOwesMoney(row);
    const badgeBg = pendingOwesMoney ? subscriptionHealthToBgColor.OVERDUE : isPendingConfirmation ? bookingStatusToBgColor[row.bookingStatus] : subscriptionHealthToBgColor[health];
    const badgeColor = pendingOwesMoney ? subscriptionHealthToColor.OVERDUE : isPendingConfirmation ? bookingStatusToColor[row.bookingStatus] : subscriptionHealthToColor[health];
    const badgeLabel = isPendingConfirmation ? row.bookingStatus : subscriptionHealthToLabel[health];
    return (
      <Box className='gbk-dg-row-phone'>
        {/* Header — health-tinted gradient */}
        <Box
          className='gbk-dg-row-phone-header'
          style={{ background: `linear-gradient(90deg, ${badgeBg}70 0%, #fafafa 55%)` }}
        >
          <span style={{ flex: 1, fontSize: '12px', color: '#555' }}>🔄 SUBSCRIPTION <b style={{ color: '#111', fontSize: '13px' }}>#{row.id}</b></span>
          <div style={{ background: badgeBg, color: badgeColor, borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
            {badgeLabel}
          </div>
        </Box>

        {/* Body */}
        <Box
          className='gbk-dg-row-phone-body'
          onClick={() => props.setParentState({ error: '', loading: false, view: 'individual', orderData: row })}
        >
          {/* Bike row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={row?.bikes?.[0]?.image} style={{ height: '40px', width: 'auto', flexShrink: 0, borderRadius: '4px' }} alt="bike" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1a1a1a' }}>
                {row?.bikes?.[0]?.name}
              </div>
              <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                {row.billingCycle ? `${billingCycleDisplayLabel[row.billingCycle]} Plan` : ''}
              </div>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
              <b style={{ fontSize: '15px', color: Colors.primary }}>₹{Number(row.totalPayableAmount).toFixed(2)}</b>
              <ArrowForwardIos style={{ fontSize: '11px', color: Colors.primary }} />
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={{ borderTop: '1px solid #f2f2f2', my: '9px' }} />

          {/* Customer info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '5px' }}>
            <Person style={{ fontSize: '14px', color: Colors.primary }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: Colors.primaryDark }}>
              {row.user?.firstName} {row.user?.lastName}
            </span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '9px' }}>
            <Phone style={{ fontSize: '14px', color: Colors.primary }} />
            <a href={`tel:${row.user?.phone}`} style={{ fontSize: '13px', color: Colors.primaryDark, fontWeight: 600, textDecoration: 'none' }}>
              {row.user?.phone}
            </a>
          </Box>

          {/* Start/End date pill - same as standard bookings */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #eef8d8 0%, #f7f7f7 100%)', border: '1px solid #ddeebb', borderRadius: '8px', padding: '6px 10px', fontSize: '11px', color: '#556', mb: '8px' }}>
            <DateRange style={{ fontSize: '13px', color: Colors.primary, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{DateTime.fromMillis(Number(row.rideStartScheduled)).toFormat('MMM d, h:mm a')}</span>
            <ArrowForwardIos style={{ fontSize: '9px', color: Colors.primary, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{DateTime.fromMillis(Number(row.rideEndScheduled)).toFormat('MMM d, h:mm a')}</span>
          </Box>

          {/* Next invoice / outstanding pill */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', background: `linear-gradient(135deg, ${outstandingToBgColor(outstandingAmount)} 0%, #f7f7f7 100%)`, border: `1px solid ${outstandingToBgColor(outstandingAmount)}`, borderRadius: '8px', padding: '6px 10px', fontSize: '11px', color: '#556' }}>
            <DateRange style={{ fontSize: '13px', color: Colors.primary, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
              {outstanding.length
                ? `Invoice due: ${DateTime.fromMillis(Number(Math.min(...outstanding.map(i => i.dueDate)))).toFormat('MMM d')}`
                : (row.nextBillingDate ? `Next invoice: ${DateTime.fromMillis(Number(row.nextBillingDate)).toFormat('MMM d')}` : '')}
            </span>
            <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap', fontWeight: 700, color: outstandingToColor(outstandingAmount) }}>
              Outstanding: ₹{outstandingAmount.toFixed(2)}
            </span>
          </Box>

          {/* Dynamic action - Send Reminder/Send Payment Link hidden for now, see TECH-296 */}
          {(health === 'PENDING' || health === 'OVERDUE') && (
            <Box sx={{ mt: '8px', display: 'flex', justifyContent: 'flex-end', gap: '14px' }}>
              <span onClick={(e) => e.stopPropagation()}>
                <RecordCashPayment order={row} backFn={() => props.refreshOrders?.()} isSubscription>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
                    Record Cash Payment
                  </span>
                </RecordCashPayment>
              </span>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return <div className={props.containerClass}>
    {/* PC Grid */}
    <DataGrid
      sx={{ display: { xs: 'none', md: 'flex' } }}
      disableColumnMenu={dgProps.disableColumnMenu}
      disableColumnFilter={dgProps.disableColumnFilter}
      className={dgProps.className}
      rowHeight={dgProps.rowHeight}
      rows={filteredList}
      onRowClick={(params) => { props.setParentState({ error: '', loading: false, view: 'individual', orderData: params.row }) }}
      columns={dgProps.columns}
      pageSize={dgProps.pageSize}
      rowsPerPageOptions={[dgProps.pageSize]}
      filterModel={dgProps.filterModel}
      columnVisibilityModel={dgProps.columnVisibilityModel}
      disableSelectionOnClick
    />
    {/* Phone Grid */}
    <DataGrid
      sx={{ display: { xs: 'flex', md: 'none' } }}
      disableColumnMenu={dgProps.disableColumnMenu}
      disableColumnFilter={dgProps.disableColumnFilter}
      className={`${dgProps.className} phone`}
      rowHeight={195}
      rows={filteredList}
      filterModel={dgProps.filterModel}
      columnVisibilityModel={dgProps.columnVisibilityModel}
      components={{
        Row: (data: { row: OrderModel }) => data.row.bookingType === BookingType.SUBSCRIPTION
          ? renderSubscriptionRow(data.row)
          : renderStandardRow(data.row)
      }}
      columns={dgProps.columns}
      pageSize={dgProps.pageSize}
      rowsPerPageOptions={[dgProps.pageSize]}
      disableSelectionOnClick
      headerHeight={0}
    />
  </div >
}

// Utility function to get order status text
function getOrderStatusText(bookingStatus: BookingStatus, rideStart: number, rideEnd: number) {
  return bookingStatus;
}

// Utility function to check if order is confirmed
function isOrderConfirmed(order: OrderModel) {
  return (
    order.bookingStatus === BookingStatus.CONFIRMED
    || order.bookingStatus === BookingStatus.EXTENDED
    || (order.bookingStatus === BookingStatus.PENDING && order.orderMode === OrderMode.VENDOR)
  )
}